import { Box } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  date as commonDate,
  finleyAssert,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  BorrowingBasePipelineSingleProgressBreakdownGQLType,
  DownloadWithLoanProductGQLType,
  StepStatusEnumGQLType,
  UrlAndFilenameGQLType,
  VerificationPipelineStatusGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import {
  useGetFundingRequestVerificationReportByMetadataLazyQueryGQLType,
  useGetLenderVerificationProgressBreakdownQuery,
} from '../requests/requests.gql.generated';
import {
  VerificationApproveAccordion,
  VerificationApproveAccordionPlaceholder,
} from './VerificationApproveAccordion';
import { VerificationCalculateAccordion } from './VerificationCalculateAccordionPanel';
import { VerificationCreditFacilityAccordionPanel } from './VerificationCreditFacilityAccordionPanel';
import { VerificationStatusIcon } from './VerificationIcons';
import {
  VerificationSingleBorrowingBaseReport,
  VerificationSingleBorrowingBaseReportPlaceholder,
} from './VerificationSingleBorrowingBaseReport';
import {
  baseNewVerificationUrl,
  reportingDateQueryParam,
  VerificationsAccordion,
  VerificationsTemplate,
} from './VerificationsTemplate';
import {
  VerificationPipelineRunId,
  useVerificationOptions,
} from './VerificationTableSharedComponents';
import { VerificationUploadAccordion } from './VerificationUploadAccordion';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';

type CurrentStep = 'selectReport' | 'upload' | 'calculate' | 'approve';

// returns query params if the baseurl is in the new verification workflow
// otherwise return null
const getQueryParamsForUploadNewVersion = ({
  query,
  queryParam,
  currentPathname,
}: {
  query: URLSearchParams;
  queryParam: string;
  currentPathname: string;
}) => {
  return currentPathname === baseNewVerificationUrl
    ? query.get(queryParam)
    : null;
};

export const VerificationWorkflow: React.FC<{
  organizationId: string;
  pipelineRunId?: VerificationPipelineRunId;
}> = ({ organizationId, pipelineRunId }) => {
  const history = useHistory();
  const { pathname: currentPathname } = useLocation();

  const { navigator, overrides, displayOptions } = useVerificationOptions();

  // queryParams may be present when starting a new verification workflow with existing presets
  const query = useQueryParams();
  const creditFacilityIdFromQueryParam = getQueryParamsForUploadNewVersion({
    query,
    queryParam: creditFacilityIdQueryParam,
    currentPathname,
  });
  const reportingDateFromQueryParam = getQueryParamsForUploadNewVersion({
    query,
    queryParam: reportingDateQueryParam,
    currentPathname,
  });
  const reportingDatefromQueryParamFullDateStr = commonTypeguards.isFullDateStr(
    reportingDateFromQueryParam,
  )
    ? reportingDateFromQueryParam
    : undefined;

  // Explicitly setting this to avoid issues with reporting date and date picker conversions
  // Internal usage expects default TZ=America/New_York
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientCurrentDate = commonDate.dateToFullDateString(
    new Date(),
    clientTimeZone,
  );

  // Indicates that the file upload has complete. Does not necessarily mean that we have moved
  // past the upload accordion, since data validations could still be happening.
  const [uploadIsComplete, setUploadIsComplete] = useState(false);
  const [calculationIsComplete, setCalculationIsComplete] = useState(false);
  const [
    verificationStatus,
    setVerificationStatus,
  ] = useState<VerificationPipelineStatusGQLType | null>(null);

  const [selectedCreditFacilityId, setSelectedCreditFacilityId] = useState<
    Util.Maybe<string>
  >(
    (overrides.fixedCreditFacilityId || creditFacilityIdFromQueryParam) ??
      undefined,
  );
  const [currentStep, setCurrentStep] = useState<CurrentStep>('selectReport');

  const [artifactDocumentId, setArtifactDocumentId] = useState<
    string | undefined
  >(undefined);
  const [selectedReportingDate, setSelectedReportingDate] = useState<
    Util.FullDateStr | undefined
  >(reportingDatefromQueryParamFullDateStr);
  const [fileVersion, setFileVersion] = useState<number>(1); // will be set on upload
  const [
    verificationReport,
    setVerificationReport,
  ] = useState<Util.SanitizedJSON | null>(null);
  const [verificationReportId, setVerificationReportId] = useState<
    string | null
  >(null);
  const [
    verificationReportBorrowingBaseReportId,
    setVerificationReportBorrowingBaseReportId,
  ] = useState<string | null>(null);
  const [remediationStatus, setRemediationStatus] = useState<{
    remediationRequested: boolean;
    remediationNote: string | null;
  } | null>(null);

  const [
    overallCalculationStatus,
    setOverallCalculationStatus,
  ] = useState<StepStatusEnumGQLType>('notStarted');
  const [progressBreakdownStatus, setProgressBreakdownStatus] = useState<
    BorrowingBasePipelineSingleProgressBreakdownGQLType[]
  >([]);
  const [
    uploadProgressBreakdownStatus,
    setUploadProgressBreakdownStatus,
  ] = useState<BorrowingBasePipelineSingleProgressBreakdownGQLType[]>([]);

  const [
    servicerReportDownload,
    setServicerReportDownload,
  ] = useState<UrlAndFilenameGQLType | null>(null);
  const [
    extendedResponseTapeDownloads,
    setExtendedResponseTapeDownloads,
  ] = useState<DownloadWithLoanProductGQLType[] | null>(null);

  const [
    getVerificationReport,
    { loading: verificationReportLoading },
  ] = useGetFundingRequestVerificationReportByMetadataLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data && data.fundingRequestVerificationReportByMetadata) {
        const parsedVerificationReport: Util.SanitizedJSON = JSON.parse(
          data.fundingRequestVerificationReportByMetadata.verificationReport,
        );
        setVerificationReport(parsedVerificationReport);
        setVerificationReportId(
          data.fundingRequestVerificationReportByMetadata.verificationReportId,
        );
        setVerificationReportBorrowingBaseReportId(
          data.fundingRequestVerificationReportByMetadata.borrowingBaseReportId,
        );
        setRemediationStatus({
          remediationRequested:
            data.fundingRequestVerificationReportByMetadata
              .remediationRequested ?? false,
          remediationNote:
            data.fundingRequestVerificationReportByMetadata.remediationNote ??
            null,
        });
      }
    },
  });

  useEffect(() => {
    setArtifactDocumentId(pipelineRunId?.artifactDocumentId);
    setSelectedCreditFacilityId(
      (overrides.fixedCreditFacilityId || creditFacilityIdFromQueryParam) ??
        pipelineRunId?.creditFacilityId,
    );
    setSelectedReportingDate(
      reportingDatefromQueryParamFullDateStr || pipelineRunId?.reportingDate,
    );
    setFileVersion(pipelineRunId?.fileVersion ?? 1);

    if (pipelineRunId) {
      // we can assume that if the pipeline has appeared in the "in progress tracker"
      // the initial raw file upload was at least completed, which kicked off the pipeline
      setUploadIsComplete(true);
    } else {
      setUploadIsComplete(false);
      setServicerReportDownload(null);
    }
  }, [pipelineRunId, clientCurrentDate]);

  const onCalculationComplete = () => {
    setCalculationIsComplete(true);

    finleyAssert(
      artifactDocumentId && selectedCreditFacilityId && selectedReportingDate,
      'artifact document and credit facility must have already been selected before calculation is complete',
    );
    getVerificationReport({
      variables: {
        artifactDocumentId,
        creditFacilityId: selectedCreditFacilityId,
        reportingDate: selectedReportingDate,
        fileVersion,
      },
    });
  };

  const {
    startPolling: startProgressPolling,
    stopPolling: stopProgressPolling,
  } = useGetLenderVerificationProgressBreakdownQuery({
    skip: Boolean(
      !artifactDocumentId ||
        !selectedCreditFacilityId ||
        !selectedReportingDate,
    ),
    variables: {
      artifactDocumentId: artifactDocumentId ?? '',
      creditFacilityId: selectedCreditFacilityId ?? '',
      reportingDate: selectedReportingDate ?? '1900-01-01',
      fileVersion,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data.lenderVerificationProgressBreakdown) {
        const progressBreakdownData = data.lenderVerificationProgressBreakdown;
        const uploadProgressStepsData =
          progressBreakdownData.uploadProgressBreakdown;
        const progressStepsData = progressBreakdownData.progressBreakdown;

        setVerificationStatus(progressBreakdownData.status);

        if (uploadIsComplete && progressBreakdownData.isRunning) {
          startProgressPolling(1000); // Only necessary for the initial page load, but harmless if already polling.
        }

        const uploadSuccess =
          uploadIsComplete &&
          uploadProgressStepsData.every(
            ({ status }) => status.status === 'success',
          );
        const calculateSuccess = progressBreakdownData.progressBreakdown.every(
          ({ status }) => status.status === 'success',
        );

        // @dani verifications - add failure state as well for isRunning === false
        if (uploadSuccess && progressBreakdownData.isRunning) {
          setOverallCalculationStatus('inProgress');
        } else if (
          uploadSuccess &&
          !progressBreakdownData.isRunning &&
          calculateSuccess
        ) {
          setOverallCalculationStatus('success');
          stopProgressPolling();
          onCalculationComplete();
        }

        if (!_.isEqual(progressBreakdownStatus, progressStepsData)) {
          setProgressBreakdownStatus(progressStepsData);
        }

        if (
          !_.isEqual(uploadProgressBreakdownStatus, uploadProgressStepsData)
        ) {
          setUploadProgressBreakdownStatus(uploadProgressStepsData);
        }

        const newServicerReportDownload =
          progressBreakdownData.servicerReportDownload ?? null;
        if (!_.isEqual(servicerReportDownload, newServicerReportDownload)) {
          setServicerReportDownload(newServicerReportDownload);
        }

        const newEtrDownload =
          progressBreakdownData.extendedResponseTapeDownloads ?? null;
        if (!_.isEqual(extendedResponseTapeDownloads, newEtrDownload)) {
          setExtendedResponseTapeDownloads(newEtrDownload);
        }
      }
    },
  });

  const onUpdateCreditFacilityAndReportSelect = ({
    creditFacilityId,
    reportingDate,
  }: {
    creditFacilityId?: string | null; // specify null to unset
    reportingDate?: Util.FullDateStr | null; // specify null to unset
  }) => {
    // Since allowed reporting dates depend on the selected credit facility, unset reporting date
    // when updating credit facility to force an explicit user selection.
    if (creditFacilityId) {
      setSelectedCreditFacilityId(creditFacilityId);
      setSelectedReportingDate(undefined);
    } else if (creditFacilityId === null) {
      setSelectedCreditFacilityId(undefined);
      setSelectedReportingDate(undefined);
    }

    if (reportingDate) {
      setSelectedReportingDate(reportingDate);
    } else if (reportingDate === null) {
      setSelectedReportingDate(undefined);
    }
  };

  const onUploadComplete = (
    uploadArtifactDocumentId: string,
    creditFacilityId: string,
    reportingDate: Util.FullDateStr,
    newFileVersion: number,
  ) => {
    navigator(history).setPipeline(
      {
        artifactDocumentId: uploadArtifactDocumentId,
        creditFacilityId,
        reportingDate,
        fileVersion: newFileVersion,
      },
      { updateInPlace: true },
    );
  };

  useEffect(() => {
    const doneWithUploadAccordion =
      uploadIsComplete &&
      uploadProgressBreakdownStatus.length > 0 &&
      uploadProgressBreakdownStatus.every(
        ({ status }) => status.status === 'success',
      );

    if (calculationIsComplete) {
      setCurrentStep('approve');
    } else if (doneWithUploadAccordion) {
      setCurrentStep('calculate');
    } else if (selectedCreditFacilityId && selectedReportingDate) {
      setCurrentStep('upload');
    } else {
      setCurrentStep('selectReport');
    }
  }, [
    selectedCreditFacilityId,
    selectedReportingDate,
    uploadIsComplete,
    uploadProgressBreakdownStatus,
    calculationIsComplete,
  ]);

  return (
    <VerificationsTemplate
      status={verificationStatus}
      selectedCreditFacilityId={selectedCreditFacilityId ?? null}
      selectedReportingDate={selectedReportingDate ?? null}
    >
      <Box height="32px" />
      <VerificationsAccordion
        header="Select Report"
        icon={
          !selectedCreditFacilityId || !selectedReportingDate ? (
            <VerificationStatusIcon status="notStarted" />
          ) : (
            <VerificationStatusIcon status="success" />
          )
        }
        isExpanded={currentStep === 'selectReport'}
      >
        <VerificationCreditFacilityAccordionPanel
          organizationId={organizationId}
          selectedCreditFacilityId={selectedCreditFacilityId}
          selectedReportingDate={selectedReportingDate}
          onUpdateCreditFacilityAndReportSelect={
            onUpdateCreditFacilityAndReportSelect
          }
          isDisabled={uploadIsComplete}
        />
      </VerificationsAccordion>

      <Box height="32px" />
      {selectedCreditFacilityId && selectedReportingDate ? (
        <VerificationUploadAccordion
          isExpanded={currentStep === 'upload'}
          selectedCreditFacilityId={selectedCreditFacilityId}
          uploadIsComplete={uploadIsComplete}
          onUploadComplete={onUploadComplete}
          selectedReportingDate={selectedReportingDate}
          servicerReportDownload={servicerReportDownload}
          uploadProgressBreakdown={uploadProgressBreakdownStatus}
        />
      ) : (
        <VerificationsAccordion
          header="Upload Report"
          icon={<VerificationStatusIcon status="notStarted" />}
          isExpanded={currentStep === 'upload'}
        >
          <InfoAlertWithDescription
            description={`Please select a ${
              overrides.fixedCreditFacilityId
                ? 'reporting date'
                : 'credit facility and reporting date'
            } to verify.`}
          />
        </VerificationsAccordion>
      )}

      <Box height="32px" />
      {selectedCreditFacilityId && selectedReportingDate && uploadIsComplete ? (
        <VerificationCalculateAccordion
          isExpanded={currentStep === 'calculate'}
          selectedCreditFacilityId={selectedCreditFacilityId}
          selectedReportingDate={selectedReportingDate}
          progressBreakdownStatus={progressBreakdownStatus}
          overallStatus={overallCalculationStatus}
        />
      ) : (
        <VerificationsAccordion
          header={`Calculate ${
            selectedReportingDate
              ? commonDate.fullDateStrToSlashedDateStr(
                  selectedReportingDate,
                  commonDate.formatSlashedDateStr,
                )
              : ''
          } Servicer Report`}
          icon={<VerificationStatusIcon status="notStarted" />}
          isExpanded={currentStep === 'calculate'}
        >
          <InfoAlertWithDescription description="Please wait until upload is complete." />
        </VerificationsAccordion>
      )}

      <Box height="32px" />
      {(() => {
        if (
          !selectedCreditFacilityId ||
          !selectedReportingDate ||
          !uploadIsComplete ||
          !calculationIsComplete ||
          !verificationReport ||
          !verificationReportId ||
          !verificationReportBorrowingBaseReportId
        ) {
          return displayOptions.hideVerification ? (
            <VerificationSingleBorrowingBaseReportPlaceholder
              reportingDate={selectedReportingDate ?? null}
              status={verificationReportLoading ? 'inProgress' : 'notStarted'}
              isExpanded={currentStep === 'approve'}
            />
          ) : (
            <VerificationApproveAccordionPlaceholder
              reportingDate={selectedReportingDate ?? null}
              status={verificationReportLoading ? 'inProgress' : 'notStarted'}
              isExpanded={currentStep === 'approve'}
            />
          );
        }

        return displayOptions.hideVerification ? (
          <VerificationSingleBorrowingBaseReport
            isExpanded={currentStep === 'approve'}
            creditFacilityId={selectedCreditFacilityId}
            reportingDate={selectedReportingDate}
            fileVersion={fileVersion}
            extendedResponseTapeDownloads={extendedResponseTapeDownloads}
          />
        ) : (
          <VerificationApproveAccordion
            isExpanded={currentStep === 'approve'}
            organizationId={organizationId}
            selectedCreditFacilityId={selectedCreditFacilityId}
            selectedReportingDate={selectedReportingDate}
            selectedFileVersion={fileVersion}
            verificationReport={verificationReport}
            verificationReportId={verificationReportId}
            verificationReportBorrowingBaseReportId={
              verificationReportBorrowingBaseReportId
            }
            artifactDocumentId={artifactDocumentId}
            canApproveOrDeny={verificationStatus === 'awaitingApproval'}
            extendedResponseTapeDownloads={extendedResponseTapeDownloads}
            servicerReportDownload={servicerReportDownload}
            remediationStatus={remediationStatus}
          />
        );
      })()}
    </VerificationsTemplate>
  );
};
